<template>
  <div class="content-wrapper view-discount-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <h4><i class="fas fa-percent"></i> จัดการส่วนลด</h4>
          <div class="row mx-1 my-3">
            <b-form-input
              type="search"
              class="col required"
              autofocus
              v-model="filter"
              placeholder="ค้นหาส่วนลดจากชื่อ"
            ></b-form-input>
            <b-button
              class="col-5 col-sm-4 col-md-3 ml-3"
              variant="primary"
              @click="openCreateModal"
            >
              <i class="fas fa-plus"></i> เพิ่มส่วนลด
            </b-button>
          </div>

          <b-table
            id="discountManageTable"
            ref="discountManageTable"
            hover
            small
            sort-icon-left
            head-variant="light"
            :items="discountList"
            :fields="fields"
            :filter="filter"
            responsive
            table-class="compact-list-table"
            show-empty
            empty-text="ไม่มีรายการส่วนลด"
          >
            <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
            <template #cell(discountType)="row">
              {{ row.item.discountType === 0 ? "เปอร์เซ็นต์" : "บาท" }}
            </template>
            <template #cell(discountValue)="row">
              <template v-if="row.item.discountMethod === 1">
                DF: {{ row.item.discountType === 0 ? `${row.item.doctorDiscountValue}%` : `${row.item.doctorDiscountValue} บาท` }}
                / CF: {{ row.item.discountType === 0 ? `${row.item.clinicDiscountValue}%` : `${row.item.clinicDiscountValue} บาท` }}
              </template>
              <template v-else>
                {{
                  row.item.discountType === 0
                    ? `${row.item.discountValue}%`
                    : `${row.item.discountValue} บาท`
                }}
              </template>
            </template>
            <template #cell(discountMethod)="row">
              {{ row.item.discountMethod === 0 ? "ทั่วไป" : "กำหนดเอง (DF/CF)" }}
            </template>
            <template #cell(treatments)="row">
              <span v-if="!row.item.treatments || row.item.treatments.length === 0" class="text-muted">
                ทุกรายการ
              </span>
              <span v-else v-b-tooltip.hover.top="getTreatmentTooltip(row.item.treatments)" class="info-badge">
                {{ row.item.treatments.length }} รายการ
                <i class="fas fa-info-circle text-muted small"></i>
              </span>
            </template>
            <template #cell(branchIds)="row">
              <span v-if="!row.item.branchIds || row.item.branchIds.length === 0" class="text-muted">
                ทุกสาขา
              </span>
              <span v-else v-b-tooltip.hover.top="getBranchTooltip(row.item.branchIds)" class="info-badge">
                {{ row.item.branchIds.length }} สาขา
                <i class="fas fa-info-circle text-muted small"></i>
              </span>
            </template>
            <template #cell(isActive)="row">
              <b-badge :variant="row.item.isActive ? 'success' : 'secondary'">
                {{ row.item.isActive ? "เปิดใช้" : "ปิดใช้" }}
              </b-badge>
            </template>
            <template v-slot:cell(buttons)="row">
              <b-button-group class="show-when-hovered" size="xs">
                <b-button
                  variant="outline-secondary"
                  @click="openEditModal(row.item)"
                >
                  <i class="fas fa-edit"></i>
                </b-button>
                <b-button
                  variant="outline-danger"
                  @click="confirmDelete(row.item)"
                >
                  <i class="fas fa-trash-alt"></i>
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </div>
      </div>
      <DiscountForm
        ref="discountForm"
        :clinic-type="clinicType"
        @saved="fetchDiscounts"
      />
      <Loading v-if="isLoading"></Loading>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DiscountForm from "@/components/discount/DiscountForm.vue";
import Loading from "@/components/Loading";

export default {
  name: "DiscountManagement",
  components: { DiscountForm, Loading },
  data() {
    return {
      filter: "",
      isLoading: false,
      fields: [
        { key: "index", label: "#", class: "text-center col-index" },
        { key: "name", label: "ชื่อส่วนลด", sortable: true, class: "col-name" },
        { key: "discountMethod", label: "ประเภท", class: "col-cate" },
        { key: "discountValue", label: "มูลค่า", class: "col-discount-value" },
        { key: "treatments", label: "รายการรักษา", class: "col-num" },
        { key: "branchIds", label: "สาขา", class: "col-num" },
        { key: "isActive", label: "สถานะ", class: "col-df" },
        { key: "buttons", label: "จัดการ", class: "text-center col-buttons" },
      ],
    };
  },
  computed: {
    ...mapGetters("moduleDiscount", ["getDiscountList"]),
    ...mapGetters("moduleAppointment", ["getTreatList", "getTreatCateList", "getItemList"]),
    ...mapGetters("moduleUser", ["getClinicList", "getBranchInfo"]),
    discountList() {
      return this.getDiscountList;
    },
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    clinicType() {
      return this.$store.state.moduleBranch?.branchInfo?.clinic?.type || "STANDALONE";
    },
    branchMap() {
      if (!this.getClinicList || !this.getBranchInfo) return {};
      const clinic = this.getClinicList.find(c => c.id === this.getBranchInfo?.clinic?.id);
      if (!clinic || !clinic.branches) return {};
      const map = {};
      clinic.branches.forEach(b => { map[b.id] = b.name; });
      return map;
    },
  },
  mounted() {
    this.fetchDiscounts();
    this.fetchMasterData();
  },
  methods: {
    ...mapActions("moduleDiscount", ["fetchDiscountList", "deleteDiscount"]),
    ...mapActions("moduleAppointment", ["fetchTreatList", "fetchTreatCateList", "fetchItemList"]),
    async fetchMasterData() {
      const payload = { clinicUrl: this.clinicUrl, branchUrl: this.branchUrl };
      await Promise.all([
        this.fetchTreatList(payload).catch(() => {}),
        this.fetchTreatCateList(payload).catch(() => {}),
        this.fetchItemList(payload).catch(() => {}),
      ]);
    },
    async fetchDiscounts() {
      this.isLoading = true;
      await this.fetchDiscountList({
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
      });
      this.isLoading = false;
    },
    formatTreatmentLabel(item) {
      if (item.treatCateId) {
        const cate = (this.getTreatCateList || []).find(c => c.id === item.treatCateId);
        return cate ? `[หมวด] ${cate.nameTh}` : `หมวด #${item.treatCateId}`;
      }
      if (item.treatId) {
        const treat = (this.getTreatList || []).find(t => t.id === item.treatId);
        return treat ? treat.nameTh : `รักษา #${item.treatId}`;
      }
      if (item.itemId) {
        const itm = (this.getItemList || []).find(i => i.id === item.itemId);
        return itm ? `[สินค้า] ${itm.nameTh}` : `สินค้า #${item.itemId}`;
      }
      return "";
    },
    getTreatmentTooltip(treatments) {
      return treatments.map(t => `• ${this.formatTreatmentLabel(t)}`).join("\n");
    },
    getBranchTooltip(branchIds) {
      return branchIds.map(id => `• ${this.branchMap[id] || `สาขา #${id}`}`).join("\n");
    },
    openCreateModal() {
      this.$refs.discountForm.show({
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
      });
    },
    openEditModal(discount) {
      this.$refs.discountForm.show({
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
        discount,
      });
    },
    async confirmDelete(discount) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `ต้องการลบส่วนลด "${discount.name}" ?`,
        { title: "ยืนยันการลบ", okVariant: "danger", okTitle: "ลบ", cancelTitle: "ยกเลิก" }
      );
      if (confirmed) {
        try {
          this.isLoading = true;
          await this.deleteDiscount({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
            id: discount.id,
          });
          this.$bvToast.toast("ลบส่วนลดสำเร็จ", {
            title: "สำเร็จ",
            variant: "success",
            autoHideDelay: 3000,
          });
          await this.fetchDiscounts();
        } catch (err) {
          this.$bvToast.toast(
            err.response?.data?.message || "ลบส่วนลดไม่สำเร็จ กรุณาลองใหม่",
            { title: "ผิดพลาด", variant: "danger", autoHideDelay: 5000 }
          );
          this.isLoading = false;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.view-discount-manage .col-discount-value {
  width: 150px;
  white-space: normal;
}
.view-discount-manage .info-badge {
  cursor: pointer;
  text-decoration: underline dotted;
  &:hover {
    color: #007bff;
  }
}
</style>
